import React, { useState, useContext, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEnvelope, faCheckCircle, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../../context/AuthProvider';
import { BundleContext } from "../../../context/BundleProvider";
import { IS_BUNDLE_APP, BUNDLE_APP_NAME, GET_APP_NAME, APP_DATA } from '../../../../app_constant';

const TOPIC_OPTIONS = [
  'General Question',
  'Bug Report',
  'Feature Request',
  'Billing',
  'Partnership',
  'Other',
];

const HIDDEN_IFRAME_NAME = 'support_form_target';

const SupportEmailModal = ({ isOpen, onClose, theme }) => {
  const { bundleData, profile, isSubscribe } = useContext(AuthContext);
  const { selectedCertificate } = useContext(BundleContext);
  const [thankYou, setThankYou] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const formRef = useRef(null);

  const authEmail = IS_BUNDLE_APP ? bundleData?.auth_email ?? '' : profile?.auth_email ?? '';
  const userName = `${bundleData?.auth_first_name ?? ""} ${bundleData?.auth_last_name ?? ""}`.trim();
  const bundleAppName = BUNDLE_APP_NAME;
  const appNameForCollection = GET_APP_NAME(selectedCertificate);

  const supportInfo = (() => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    let os = 'Unknown';
    if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    const platform = `Web (${browser} / ${os})`;
    const loginProvider = (IS_BUNDLE_APP ? bundleData?.auth_login_provider : profile?.auth_login_provider) || 'Email';
    return [
      '--- Support Info ---',
      `App: ${APP_DATA.DISPLAY_APP_NAME}`,
      `Exam: ${appNameForCollection}`,
      `Plan: ${isSubscribe ? 'Premium' : 'Free'}`,
      `Platform: ${platform}`,
      `Login: ${loginProvider}`,
      '--- End Support Info ---',
    ].join('\n');
  })();

  const [formData, setFormData] = useState({
    name: userName,
    email: authEmail,
    topic: 'General Question',
    message: '',
  });

  // Update pre-filled values when context changes
  const prevEmail = useRef(authEmail);
  const prevName = useRef(userName);
  if (prevEmail.current !== authEmail || prevName.current !== userName) {
    prevEmail.current = authEmail;
    prevName.current = userName;
    setFormData(prev => ({ ...prev, name: userName, email: authEmail }));
  }

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleIframeLoad = useCallback(() => {
    // The iframe onLoad fires once on mount (about:blank) and again after form POST.
    // We only transition to thank-you when we're in submitting state.
    if (submitting) {
      setSubmitting(false);
      setThankYou(true);
    }
  }, [submitting]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Submit the real form into the hidden iframe
    formRef.current?.submit();
  };

  const blue = theme?.primary?.blue?.[600] || '#2563eb';
  const inputBorder = theme?.borders?.primary || '#d1d5db';

  const inputStyle = (field) => ({
    width: '100%',
    padding: '0.7rem 0.85rem',
    border: `1.5px solid ${focusedField === field ? blue : inputBorder}`,
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    background: theme?.backgrounds?.secondary || '#f9fafb',
    color: theme?.text?.primary || '#111',
    outline: 'none',
    boxShadow: focusedField === field ? `0 0 0 3px ${blue}33` : 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
  });

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: theme?.text?.primary || '#111',
    marginBottom: '0.35rem',
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2rem',
      maxWidth: '560px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem', right: '1rem',
      width: '2rem', height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      zIndex: 10,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1.5rem',
    },
    icon: { fontSize: '1.5rem', color: blue },
    title: { fontSize: '1.5rem', fontWeight: '700', color: theme?.text?.primary },
    thankYouContainer: { textAlign: 'center', padding: '3rem' },
    thankYouIcon: {
      fontSize: '4rem',
      color: theme?.dashboard?.status?.correct || '#10b981',
      marginBottom: '1rem',
    },
    thankYouTitle: { fontSize: '1.5rem', fontWeight: '700', color: theme?.text?.primary, marginBottom: '0.5rem' },
    thankYouMessage: { fontSize: '1rem', color: theme?.text?.secondary },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button style={styles.closeButton} onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>

            {thankYou ? (
              <motion.div
                style={styles.thankYouContainer}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={styles.thankYouIcon} />
                <h3 style={styles.thankYouTitle}>Thank You!</h3>
                <p style={styles.thankYouMessage}>
                  Your message has been sent successfully. We'll get back to you soon.
                </p>
              </motion.div>
            ) : (
              <>
                <div style={styles.header}>
                  <FontAwesomeIcon icon={faEnvelope} style={styles.icon} />
                  <h2 style={styles.title}>Contact Support</h2>
                </div>

                {/* Hidden iframe as form POST target to avoid CORS */}
                <iframe
                  name={HIDDEN_IFRAME_NAME}
                  title="form-target"
                  onLoad={handleIframeLoad}
                  style={{ display: 'none' }}
                />

                <form
                  ref={formRef}
                  action="https://www.learnzapp.com/php/web_contact.php"
                  method="POST"
                  target={HIDDEN_IFRAME_NAME}
                  onSubmit={handleSubmit}
                >
                  {/* Hidden fields */}
                  <input type="hidden" name="bundle_app_name" value={bundleAppName} />
                  <input type="hidden" name="app_name_for_collection" value={appNameForCollection} />
                  <input type="hidden" name="support_info" value={supportInfo} />

                  {/* Name & Email row */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={labelStyle}>
                        Name <span style={{ color: '#f97316' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        style={inputStyle('name')}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        Email <span style={{ color: '#f97316' }}>*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => { setFormData(prev => ({ ...prev, email: e.target.value })); }}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        style={inputStyle('email')}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Topic */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={labelStyle}>
                      Topic <span style={{ color: '#f97316' }}>*</span>
                    </label>
                    <select
                      name="topic"
                      required
                      value={formData.topic}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('topic')}
                      onBlur={() => setFocusedField(null)}
                      style={{ ...inputStyle('topic'), appearance: 'auto' }}
                    >
                      {TOPIC_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={labelStyle}>
                      Message <span style={{ color: '#f97316' }}>*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      style={{ ...inputStyle('message'), resize: 'vertical' }}
                      placeholder="How can we help you?"
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      background: submitting ? '#93c5fd' : blue,
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'background 0.2s',
                    }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {/* Disclaimer */}
                  <p style={{
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    color: theme?.text?.tertiary || '#9ca3af',
                    marginTop: '0.75rem',
                    marginBottom: 0,
                  }}>
                    We'll reply to your email address. No spam, ever.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SupportEmailModal;
